import React, { useEffect, useState } from "react";
import { FmPageTitle, FmUnderlineNav, FmBack, FmNutrientsTable, FmWarningBox, FmButton, FmBlackPopup } from "../../../Components";
import { useIsMobile } from "../../../Hooks";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { GeneralState, AgriSolutionState, RootState } from "../../../Redux";
import { LOGGED_IN_ROUTES } from "../../../Constants";
import { useHistory } from "react-router-dom";
import { FmAllModals } from "../../../Components/Modals";

import useStyles from "./FarmStagePage.styles";
import { Actions } from "../../../Redux/States/AgriSolution/AgriSolutionSlice";
import { DEALER_LINK } from "../../../Utils/Consts";
import { BottomSection } from "../Components";
import { FmCarbonFootprintBanner } from "../../../Components/FmCarbonFootprintBanner";
import { ShowWhen } from "../../../Components/ShowWhen";
import { Api } from "../../../Services";

const FarmStagePage = () => {
  const classes = useStyles();
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();

  const { cropDemand, farmerPractice } = useSelector(AgriSolutionState.Selectors.selectFarmState);

  const [input1, setInput1] = useState<number | string>(farmerPractice.n as number);
  const [input2, setInput2] = useState<number | string>(farmerPractice.p as number);
  const [input3, setInput3] = useState<number | string>(farmerPractice.k as number);

  const [showClearStepModal, setShowClearStepModal] = useState<boolean>(false);
  const [showSaveItemModal, setShowSaveItemModal] = useState<boolean>(false);
  const [hasSaveButton, setHasSaveButton] = useState<boolean>(false);
  const [hasClearButton, setHasClearButton] = useState<boolean>(false);
  const [ShowBlackSnackbar, setShowBlackSnackbar] = useState<boolean>(false);
  const [showBanner, setBannerShown] = useState<boolean>(true);
  const isUserSave = useSelector(AgriSolutionState.Selectors.selectIsUserSave);
  const itemId = useSelector((state: RootState) => state.agriSolution.itemId);

  const showClearThisStep = useSelector(GeneralState.Selectors.selectShowClearThisStepModal);

  const inputsArray = [
    {
      input: input1,
      setInput: setInput1,
    },
    {
      input: input2,
      setInput: setInput2,
    },
    {
      input: input3,
      setInput: setInput3,
    },
  ];

  useEffect(() => {
    (window as any).dataLayer.push({
      event: "FarmStage",
      pageUrl: "https://multimatch.haifa-group.com/multi-match/agri-solution/farm",
      pageTitle: "FarmStage",
    });
  }, []);

  const startingN = input1;
  const startingK = input2;
  const startingP = input3;

  useEffect(() => {
    const allHasValues = input1 !== startingN && input2 !== startingK && input3 !== startingP;
    const anyHasValues = !!(
      parseInt(input1 as string) !== startingN ||
      parseInt(input2 as string) !== startingP ||
      parseInt(input3 as string) !== startingK
    );

    dispatch(
      Actions.setFarmerPractice({
        n: parseInt(input1 as string) as number,
        p: parseInt(input3 as string) as number,
        k: parseInt(input2 as string) as number,
      }),
    );
    setHasClearButton(anyHasValues);
    setHasSaveButton(anyHasValues);
  }, [input1, input2, input3]);

  //------------ Modals ---------------------//

  const closeSaveItemModalHandle = () => {
    setShowSaveItemModal(false);
  };
  const openSaveItemModalHandle = () => {
    setShowSaveItemModal(true);
  };
  const closeClearModalHandle = () => {
    setShowClearStepModal(false);
  };
  const openClearModalHandle = () => {
    setShowClearStepModal(true);
  };
  //-----------------------------------------//

  const handleClear = () => {
    setInput1("");
    setInput2("");
    setInput3("");
  };

  const onClickNextHandler = async () => {
    const payload = {
      N: parseFloat(input1 as string),
      P: parseFloat(input3 as string),
      K: parseFloat(input2 as string),
    };

    if (!itemId) {
      throw new Error("Invalid item id saved");
    }

    const response = await Api.SavedItems.updateFarmerPractice(itemId, payload);
    console.log(response.data);

    setHasSaveButton(false);
    setTimeout(() => {
      history.push(LOGGED_IN_ROUTES.AGRI_SOLUTION_FIFTH_STEP);
    }, 300);
  };

  const onClickBackHandler = () => {
    history.push(LOGGED_IN_ROUTES.AGRI_SOLUTION_THIRD_STEP);
  };

  const saveSolutionHandler = async () => {
    setHasSaveButton(false);
    await dispatch(AgriSolutionState.Actions.setIsUserSave(true));
    await dispatch(AgriSolutionState.Thunks.saveProcess());

    if (isUserSave) {
      setShowBlackSnackbar(true);
    }

    dispatch(
      Actions.setFarmerPractice({
        n: parseInt(input1 as string) as number,
        p: parseInt(input2 as string) as number,
        k: parseInt(input3 as string) as number,
      }),
    );
  };

  const closeSnackbarHandler = () => {
    setShowBlackSnackbar(false);
  };

  return (
    <>
      {ShowBlackSnackbar && (
        <div className={classes.snackbar_container}>
          <FmBlackPopup text={t("saveItemPopup.snackbar")} snackbar={true} onClose={closeSnackbarHandler} />
        </div>
      )}
      <div className={classes.container}>
        <div className={isMobile ? classes.title_container_mobile : classes.title_container}>
          <FmPageTitle title={t("pageStep4.title")} />
          <FmUnderlineNav
            title={t("saveItemPopup.save")}
            onClick={
              hasSaveButton && !isUserSave
                ? openSaveItemModalHandle
                : hasSaveButton && isUserSave
                  ? saveSolutionHandler
                  : () => {
                      //
                    }
            }
            disabled={!hasSaveButton}
          />
        </div>
        <div className={isMobile ? classes.mobile_practiceDescription : classes.practiceDescription}>
          <span>{t("pageStep4.question")}</span>
          <span className={classes.mediumBottomMargin}>{t("pageStep4.questionDescription")}</span>
          <span className={isMobile ? classes.mobile_fill_container : classes.smallBottomMargin}>{t("pageStep4.fill")}</span>
        </div>

        <ShowWhen when={showBanner}>
          <FmCarbonFootprintBanner description={t("pageStep4.cfpBanner")} onClose={() => setBannerShown(false)} />
        </ShowWhen>

        <div className={classes.NutrientsTable_container}>
          <FmNutrientsTable inputsArray={inputsArray} cropDemand={cropDemand} />
        </div>
        <div className={isMobile ? classes.mobile_secondDescription : classes.secondDescription}>
          <span>{t("pageStep4.buttomDescription")}</span>
        </div>
        <div className={isMobile ? classes.mobile_warningMessage_container : classes.warningMessage_container}>
          <FmWarningBox
            icon={"/multi-match/assets/envelope.png"}
            description={
              <>
                {t("pageStep4.warningText1")}
                <a className={classes.boldUnderline} href={DEALER_LINK} target="_blank" rel="noreferrer noopener">
                  {t("pageStep4.warningText2")}
                </a>
                {t("pageStep4.warningText3")}
              </>
            }
          />
        </div>
        <div className={classes.footer}>
          <BottomSection
            onClear={showClearThisStep ? openClearModalHandle : handleClear}
            canSubmit
            onSubmit={onClickNextHandler}
            onBackButton={onClickBackHandler}
          />
        </div>
        <FmAllModals
          clearModalIsOpen={showClearStepModal}
          clearModalOnClose={closeClearModalHandle}
          clearModalAction={handleClear}
          saveItemModalIsOpen={showSaveItemModal}
          saveItemModalOnClose={closeSaveItemModalHandle}
          saveItemModalAction={saveSolutionHandler}
          SaveProcessPromptWhen={hasSaveButton}
          SaveProcessPromptMessage={t("saveProcessPopup.subTitle")}
        />
      </div>
    </>
  );
};

export default FarmStagePage;
